import React, { useState, useEffect, useCallback, useMemo } from 'react';
import logger from '../../utils/logger';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import {
  Role,
  BusinessPartner,
  BusinessPartnerDataSource,
  BusinessPartnerStatus,
  BusinessPartnerRole,
} from '../../types';
import PageTitle from '../../components/ui/PageTitle';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { PaginatedTable, PaginatedCards } from '../../components/ui';
import {
  BuildingOfficeIcon,
  PlusIcon,
  MagnifyingGlassIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import { useMasterData } from '../../hooks/useMasterData';
import { BusinessPartnerFormModal } from '../../components/business-partner/BusinessPartnerFormModal';
import { PartnerDetailModal } from '../../components/modals/PartnerDetailModal';
import { SupabaseDeadlineRuleService } from '../../services/supabase/supabaseDeadlineRuleService';

const BusinessPartnerMasterPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    businessPartners,
    companies,
    refreshBusinessPartners,
    addBusinessPartner,
    updateBusinessPartner,
  } = useMasterData();

  // State管理
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDataSource, setSelectedDataSource] = useState<BusinessPartnerDataSource | 'all'>(
    'all'
  );
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<BusinessPartner | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    partnerId: string;
    partnerName: string;
  } | null>(null);
  const [deadlineRulesMap, setDeadlineRulesMap] = useState<Record<string, number>>({});
  const [isLoadingDeadlineRules, setIsLoadingDeadlineRules] = useState(false);

  // 権限チェック
  useEffect(() => {
    if (user && user.role !== Role.SUPER_ADMIN) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  // 締切ルールの読み込み
  useEffect(() => {
    const loadDeadlineRules = async () => {
      if (!user?.companyId) return;

      setIsLoadingDeadlineRules(true);
      try {
        const deadlineRuleService = new SupabaseDeadlineRuleService();
        const allRules = await deadlineRuleService.getAllDeadlineRules(user.companyId);

        // 取引先IDごとの締切ルール数をマップに格納
        const rulesCountMap: Record<string, number> = allRules.reduce(
          (acc: Record<string, number>, rule: { businessPartnerId: string }) => {
            acc[rule.businessPartnerId] = (acc[rule.businessPartnerId] || 0) + 1;
            return acc;
          },
          {}
        );

        setDeadlineRulesMap(rulesCountMap);
      } catch (err) {
        logger.error('締切ルールの読み込みに失敗しました:', err);
        setError('締切ルール情報の読み込みに失敗しました。ページを再読み込みしてください。');
        setDeadlineRulesMap({});
      } finally {
        setIsLoadingDeadlineRules(false);
      }
    };

    loadDeadlineRules();
  }, [user?.companyId]);

  // フィルタリングされた取引先一覧
  const filteredPartners = useMemo(() => {
    return businessPartners.filter(partner => {
      const matchesSearch = partner.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDataSource =
        selectedDataSource === 'all' || partner.dataSource === selectedDataSource;

      return matchesSearch && matchesDataSource;
    });
  }, [businessPartners, searchTerm, selectedDataSource]);

  // ラクハイ提供データの統計
  const rakuhaiProvidedCount = useMemo(() => {
    return businessPartners.filter(
      partner => partner.dataSource === BusinessPartnerDataSource.RAKUHAI_PROVIDED
    ).length;
  }, [businessPartners]);

  // 新規取引先追加
  const handleAddPartner = useCallback(() => {
    setSelectedPartner(null);
    setIsAddModalOpen(true);
  }, []);

  // 取引先詳細表示
  const handleViewPartner = useCallback((partner: BusinessPartner) => {
    setSelectedPartner(partner);
    setIsViewModalOpen(true);
  }, []);

  // 取引先削除
  const handleDeletePartner = useCallback(
    async (partnerId: string) => {
      // 削除対象の取引先情報を取得
      const targetPartner = businessPartners.find(p => p.id === partnerId);

      // ラクハイ管理者は独自追加データを削除できない
      if (targetPartner && targetPartner.dataSource === BusinessPartnerDataSource.CUSTOM) {
        logger.warn('SUPER_ADMINは独自追加の取引先を削除できません', {
          partnerId,
          dataSource: targetPartner.dataSource,
        });
        return;
      }

      // 削除確認モーダルを表示
      setDeleteConfirm({
        show: true,
        partnerId,
        partnerName: targetPartner?.name || '',
      });
    },
    [businessPartners]
  );

  // 削除確認後の処理
  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;

    try {
      setIsLoading(true);
      setError('');

      // TODO: 削除API実装
      logger.info('削除対象:', deleteConfirm.partnerId);

      await refreshBusinessPartners();
      setDeleteConfirm(null);
    } catch (err) {
      logger.error('取引先削除エラー:', err);
      setError('取引先の削除に失敗しました');
    } finally {
      setIsLoading(false);
    }
  }, [deleteConfirm, refreshBusinessPartners]);

  // 取引先保存（新規追加・編集共通）
  const handleSavePartner = useCallback(
    async (partnerData: Partial<BusinessPartner>) => {
      try {
        setError('');

        if (partnerData.id) {
          // 編集の場合
          logger.info('取引先更新:', partnerData);
          await updateBusinessPartner(partnerData.id, partnerData);
        } else {
          // 新規追加の場合
          const newPartner: BusinessPartner = {
            id: crypto.randomUUID(),
            name: partnerData.name!,
            displayName: partnerData.displayName,
            type: partnerData.type!,
            status: partnerData.status!,
            dataSource: BusinessPartnerDataSource.RAKUHAI_PROVIDED, // ラクハイ管理者が追加するため
            category: partnerData.category!,
            availableRoles: partnerData.availableRoles!,
            locations: partnerData.locations || [],
            defaultAddress: partnerData.defaultAddress!,
            defaultPhone: partnerData.defaultPhone!,
            defaultContactPerson: partnerData.defaultContactPerson || '',
            defaultEmail: partnerData.defaultEmail || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            notes: partnerData.notes,
            isSystemPreset: false,
          };

          logger.info('新規取引先追加:', newPartner);
          await addBusinessPartner(newPartner);
        }

        logger.info('✅ 取引先保存完了');
      } catch (err) {
        logger.error('取引先保存エラー:', err);
        setError(err instanceof Error ? err.message : '取引先の保存に失敗しました');
        throw err;
      }
    },
    [addBusinessPartner, updateBusinessPartner]
  );

  // モーダル制御
  const handleCloseModals = useCallback(() => {
    setIsAddModalOpen(false);
    setIsEditModalOpen(false);
    setIsViewModalOpen(false);
    setSelectedPartner(null);
  }, []);

  const handleEditFromDetail = useCallback((partner: BusinessPartner) => {
    // ラクハイ管理者は独自追加データを編集できない
    if (partner.dataSource === BusinessPartnerDataSource.CUSTOM) {
      logger.warn('SUPER_ADMINは独自追加の取引先を編集できません', {
        partnerId: partner.id,
        dataSource: partner.dataSource,
      });
      return;
    }

    setSelectedPartner(partner);
    setIsViewModalOpen(false);
    setIsEditModalOpen(true);
  }, []);

  // 登録会社名取得
  const getCompanyName = (companyId?: string) => {
    if (!companyId) return '-';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : '不明';
  };

  // 対応可能役割のラベル取得
  const getRoleLabel = (role: BusinessPartnerRole): string => {
    const labels = {
      [BusinessPartnerRole.BILLING]: '請求先',
      [BusinessPartnerRole.PICKUP]: '引き取り先',
      [BusinessPartnerRole.PICKUP_LOCATION]: '引取拠点',
      [BusinessPartnerRole.DELIVERY]: '搬入先',
      [BusinessPartnerRole.DELIVERY_LOCATION]: '搬入拠点',
      [BusinessPartnerRole.BOTH]: '引取・搬入両方',
    };
    return labels[role] || role;
  };

  // BusinessPartnerをPartnerDetailModal用の形式に変換
  const convertBusinessPartnerToPartnerFormat = (partner: BusinessPartner) => {
    return {
      id: partner.id,
      companyName: partner.name,
      displayName: partner.displayName,
      address: partner.defaultAddress,
      contactPerson: partner.defaultContactPerson,
      email: partner.defaultEmail,
      phone: partner.defaultPhone,
      status:
        partner.status === BusinessPartnerStatus.ACTIVE
          ? ('Active' as const)
          : ('Inactive' as const),
      category: partner.category,
      source: 'businessPartner' as const,
      dataSource: partner.dataSource,
      originalType: partner.type,
    };
  };

  if (!user || user.role !== Role.SUPER_ADMIN) {
    return <div>権限がありません</div>;
  }

  return (
    <div className='min-h-screen bg-gray-50 dark:bg-gray-900'>
      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8'>
        <PageTitle icon={BuildingOfficeIcon} title='取引先マスタ管理' />

        <p className='text-sm text-gray-600 dark:text-gray-400 mb-6'>
          ラクハイ管理者用 - 統一取引先データの管理
        </p>

        {error && (
          <div className='mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
            <div className='flex items-center'>
              <ExclamationTriangleIcon className='h-5 w-5 text-red-400 mr-2' />
              <p className='text-sm text-red-800 dark:text-red-200'>{error}</p>
            </div>
          </div>
        )}

        {/* 統計カード */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-8'>
          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <BuildingOfficeIcon className='h-8 w-8 text-blue-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      総取引先数
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {businessPartners.length}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <BuildingOfficeIcon className='h-8 w-8 text-green-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      ラクハイ提供データ
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {rakuhaiProvidedCount}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          <div className='bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg'>
            <div className='p-5'>
              <div className='flex items-center'>
                <div className='flex-shrink-0'>
                  <BuildingOfficeIcon className='h-8 w-8 text-purple-600' />
                </div>
                <div className='ml-5 w-0 flex-1'>
                  <dl>
                    <dt className='text-sm font-medium text-gray-500 dark:text-gray-400 truncate'>
                      独自登録データ
                    </dt>
                    <dd className='text-lg font-medium text-gray-900 dark:text-white'>
                      {businessPartners.length - rakuhaiProvidedCount}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 検索・フィルタ・アクション */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg mb-8'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
              <div className='flex flex-col sm:flex-row gap-4 flex-1'>
                <div className='relative flex-1 min-w-0'>
                  <div className='absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none'>
                    <MagnifyingGlassIcon className='h-5 w-5 text-gray-400' />
                  </div>
                  <Input
                    type='text'
                    placeholder='取引先名で検索...'
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className='pl-10'
                  />
                </div>
                <div className='sm:w-48'>
                  <Select
                    value={selectedDataSource}
                    onChange={e =>
                      setSelectedDataSource(e.target.value as BusinessPartnerDataSource | 'all')
                    }
                    options={[
                      { value: 'all', label: 'すべての登録元' },
                      { value: BusinessPartnerDataSource.RAKUHAI_PROVIDED, label: 'ラクハイ提供' },
                      { value: BusinessPartnerDataSource.CUSTOM, label: '独自追加' },
                    ]}
                    placeholder=''
                  />
                </div>
              </div>
              <Button onClick={handleAddPartner} icon={PlusIcon} disabled={isLoading}>
                新規取引先追加
              </Button>
            </div>
          </div>
        </div>

        {/* 取引先一覧 */}
        <div className='bg-white dark:bg-gray-800 shadow rounded-lg'>
          <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
            <h3 className='text-lg font-medium text-gray-900 dark:text-white'>
              取引先一覧 ({filteredPartners.length}件)
            </h3>
          </div>

          {filteredPartners.length === 0 ? (
            <div className='text-center py-12'>
              <BuildingOfficeIcon className='mx-auto h-12 w-12 text-gray-400 mb-4' />
              <p className='text-gray-500 dark:text-gray-400'>取引先が見つかりません</p>
              <p className='text-sm text-gray-400 dark:text-gray-500 mt-2'>
                検索条件を変更するか、新しい取引先を追加してください
              </p>
            </div>
          ) : (
            <>
              {/* デスクトップ表示：テーブル */}
              <div className='hidden lg:block p-6'>
                <PaginatedTable
                  data={filteredPartners}
                  loading={isLoading}
                  storageKey='businessPartnerList'
                  onRowClick={partner => handleViewPartner(partner)}
                  columns={[
                    {
                      key: 'name',
                      label: '取引先名',
                      render: (partner: BusinessPartner) => (
                        <div className='flex items-center'>
                          <div className='flex-1'>
                            <div className='flex items-center gap-2'>
                              <div className='text-sm font-medium text-gray-900 dark:text-white'>
                                {partner.name}
                              </div>
                              {partner.dataSource === BusinessPartnerDataSource.CUSTOM && (
                                <span className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'>
                                  独自追加: {getCompanyName(partner.companyId)}
                                </span>
                              )}
                            </div>
                            {partner.displayName && (
                              <div className='text-sm text-gray-500 dark:text-gray-400'>
                                {partner.displayName}
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    },
                    {
                      key: 'roles',
                      label: '対応可能役割',
                      render: (partner: BusinessPartner) => (
                        <div className='flex flex-wrap gap-1'>
                          {(partner.availableRoles ?? []).map(role => (
                            <span
                              key={role}
                              className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                            >
                              {getRoleLabel(role)}
                            </span>
                          ))}
                          {(!partner.availableRoles || partner.availableRoles.length === 0) && (
                            <span className='text-sm text-gray-500 dark:text-gray-400'>未設定</span>
                          )}
                        </div>
                      ),
                    },
                    {
                      key: 'deadline_rules',
                      label: '締切ルール',
                      render: (partner: BusinessPartner) => {
                        const count = deadlineRulesMap[partner.id] || 0;
                        return (
                          <div className='flex items-center gap-2'>
                            <CalendarDaysIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
                            <span className='text-sm text-gray-900 dark:text-white'>
                              {isLoadingDeadlineRules ? (
                                <span className='text-gray-400 dark:text-gray-500'>読込中...</span>
                              ) : count > 0 ? (
                                <span className='font-medium'>{count}件</span>
                              ) : (
                                <span className='text-gray-500 dark:text-gray-400'>未設定</span>
                              )}
                            </span>
                          </div>
                        );
                      },
                    },
                  ]}
                  rowKey='id'
                  emptyMessage='取引先が見つかりません'
                />
              </div>

              {/* モバイル表示：カード */}
              <div className='lg:hidden p-6'>
                <PaginatedCards
                  data={filteredPartners}
                  loading={isLoading}
                  storageKey='businessPartnerList'
                  keyExtractor='id'
                  emptyMessage='取引先が見つかりません'
                  renderCard={partner => (
                    <div
                      className='bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 sm:p-5 card-hover card-touch touch-target fade-in cursor-pointer border border-gray-200 dark:border-gray-700'
                      onClick={() => handleViewPartner(partner)}
                    >
                      <div className='flex justify-between items-start mb-3'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex flex-col gap-2'>
                            <h3 className='text-base sm:text-lg font-bold text-gray-800 dark:text-white truncate'>
                              {partner.name}
                            </h3>
                            {partner.dataSource === BusinessPartnerDataSource.CUSTOM && (
                              <div className='flex flex-wrap gap-1'>
                                <span className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300'>
                                  独自追加: {getCompanyName(partner.companyId)}
                                </span>
                              </div>
                            )}
                            {partner.displayName && (
                              <p className='text-sm text-gray-500 dark:text-gray-400 truncate'>
                                {partner.displayName}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className='space-y-2'>
                        {/* 対応可能役割 */}
                        <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                          <span className='font-medium w-20 sm:w-24 flex-shrink-0'>対応役割:</span>
                          <div className='flex flex-wrap gap-1 flex-1'>
                            {(partner.availableRoles ?? []).map(role => (
                              <span
                                key={role}
                                className='inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
                              >
                                {getRoleLabel(role)}
                              </span>
                            ))}
                            {(!partner.availableRoles || partner.availableRoles.length === 0) && (
                              <span className='text-xs text-gray-500 dark:text-gray-400'>
                                未設定
                              </span>
                            )}
                          </div>
                        </div>

                        {/* 締切ルール */}
                        <div className='flex items-start text-xs sm:text-sm text-gray-600 dark:text-gray-300'>
                          <span className='font-medium w-20 sm:w-24 flex-shrink-0'>
                            締切ルール:
                          </span>
                          <div className='flex items-center gap-1 flex-1'>
                            <CalendarDaysIcon className='h-4 w-4 text-gray-500 dark:text-gray-400' />
                            {isLoadingDeadlineRules ? (
                              <span className='text-xs text-gray-400 dark:text-gray-500'>
                                読込中...
                              </span>
                            ) : (deadlineRulesMap[partner.id] || 0) > 0 ? (
                              <span className='text-xs font-medium text-gray-900 dark:text-white'>
                                {deadlineRulesMap[partner.id]}件
                              </span>
                            ) : (
                              <span className='text-xs text-gray-500 dark:text-gray-400'>
                                未設定
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 新規追加モーダル */}
      <BusinessPartnerFormModal
        isOpen={isAddModalOpen}
        onClose={handleCloseModals}
        onSave={handleSavePartner}
        mode='create'
      />

      {/* 編集モーダル */}
      <BusinessPartnerFormModal
        isOpen={isEditModalOpen}
        onClose={handleCloseModals}
        onSave={handleSavePartner}
        partner={selectedPartner}
        mode='edit'
      />

      {/* 詳細表示モーダル */}
      <PartnerDetailModal
        isOpen={isViewModalOpen}
        onClose={handleCloseModals}
        partner={selectedPartner ? convertBusinessPartnerToPartnerFormat(selectedPartner) : null}
        businessPartner={selectedPartner || undefined}
        companyName={selectedPartner ? getCompanyName(selectedPartner.companyId) : undefined}
        onEdit={handleEditFromDetail}
        onDelete={handleDeletePartner}
      />

      {/* 削除確認モーダル */}
      {deleteConfirm?.show && (
        <div className='fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50'>
          <div className='bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6'>
            <div className='flex items-center mb-4'>
              <TrashIcon className='h-6 w-6 text-red-600 dark:text-red-400 mr-3' />
              <h3 className='text-lg font-semibold text-gray-900 dark:text-white'>削除の確認</h3>
            </div>
            <p className='text-sm text-gray-600 dark:text-gray-300 mb-2'>
              取引先「{deleteConfirm.partnerName}」を削除しますか？
            </p>
            <p className='text-xs text-gray-500 dark:text-gray-400 mb-6'>
              ※稼働中の案件で使用されている場合は削除できません。
            </p>
            <div className='flex justify-end space-x-3'>
              <Button variant='secondary' onClick={() => setDeleteConfirm(null)} size='md'>
                キャンセル
              </Button>
              <Button variant='danger' onClick={confirmDelete} size='md' disabled={isLoading}>
                <TrashIcon className='h-4 w-4 mr-2' />
                削除する
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessPartnerMasterPage;
